import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../../components/ui/dialog";
import { Textarea } from "../../../components/ui/textarea";
import { Label } from "../../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";
import { ShieldCheck } from "lucide-react";
import { MKT, getAdminAuth } from "./AdminEmailMarketing";

const FIELD_OPTIONS = ["email", "first_name", "last_name", "company_name", "phone", "website", "industry", "city", "state", "country", "source", "notes"];

export default function ImportDialog({ open, onOpenChange, lists, onImported }) {
  const [mode, setMode] = useState("csv"); // csv | paste
  const [csvText, setCsvText] = useState("");
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [targetList, setTargetList] = useState("none");
  const [pasteText, setPasteText] = useState("");
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState(false);
  const [verifInfo, setVerifInfo] = useState(null); // {queued, job_ids}
  const [verifJobs, setVerifJobs] = useState([]);

  const reset = () => { setCsvText(""); setPreview(null); setMapping({}); setSummary(null); setPasteText(""); setVerifInfo(null); setVerifJobs([]); };

  // Poll verification job progress while the dialog shows the import summary
  useEffect(() => {
    if (!open || !verifInfo?.job_ids?.length) return;
    let active = true;
    let timer;
    const tick = async () => {
      try {
        const r = await axios.get(`${MKT}/verification/jobs?ids=${verifInfo.job_ids.join(",")}`, getAdminAuth());
        if (!active) return;
        setVerifJobs(r.data.jobs);
        if (r.data.any_active) timer = setTimeout(tick, 3000);
      } catch (e) { /* transient */ }
    };
    tick();
    return () => { active = false; clearTimeout(timer); };
  }, [open, verifInfo]);

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setCsvText(reader.result);
    reader.readAsText(file);
  };

  const doPreview = async (mappingArg = null) => {
    setBusy(true);
    try {
      const body = { csv_text: csvText };
      if (mappingArg) body.mapping = mappingArg;
      const res = await axios.post(`${MKT}/contacts/import-preview`, body, getAdminAuth());
      setPreview(res.data);
      setMapping(mappingArg || res.data.detected_mapping || {});
    } catch (e) { toast.error(e.response?.data?.detail || "Preview failed"); }
    setBusy(false);
  };

  const doImport = async () => {
    setBusy(true);
    try {
      const body = { csv_text: csvText, mapping, list_ids: targetList !== "none" ? [targetList] : [], verify_after_import: true };
      const res = await axios.post(`${MKT}/contacts/import`, body, getAdminAuth());
      setSummary(res.data.summary);
      if (res.data.verification_queued) {
        setVerifInfo({ queued: res.data.verification_queued, job_ids: res.data.verification_job_ids || [] });
      }
      onImported?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Import failed"); }
    setBusy(false);
  };

  const doPasteImport = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${MKT}/contacts/paste-import`,
        { emails_text: pasteText, list_ids: targetList !== "none" ? [targetList] : [] }, getAdminAuth());
      setSummary(res.data.summary);
      onImported?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Import failed"); }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="import-dialog">
        <DialogHeader><DialogTitle>Import Contacts</DialogTitle></DialogHeader>

        {summary ? (
          <div className="space-y-3" data-testid="import-summary">
            <p className="text-sm font-semibold text-foreground">Import complete</p>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="p-2 rounded bg-emerald-500/10 text-emerald-500">Added: {summary.added}</div>
              <div className="p-2 rounded bg-amber-500/10 text-amber-500">Duplicates: {summary.duplicates}</div>
              <div className="p-2 rounded bg-red-500/10 text-red-400">Invalid: {summary.invalid}</div>
              <div className="p-2 rounded bg-slate-500/10 text-slate-400">Already suppressed: {summary.already_suppressed}</div>
              <div className="p-2 rounded bg-slate-500/10 text-slate-400">Skipped: {summary.skipped}</div>
            </div>
            <p className="text-[11px] text-muted-foreground">Suppressed addresses stay suppressed — importing never re-subscribes anyone.</p>
            {verifInfo && (
              <div className="rounded border border-blue-500/30 bg-blue-500/5 p-3 space-y-2" data-testid="import-verification-progress">
                <p className="text-xs font-semibold text-foreground flex items-center gap-1.5">
                  <ShieldCheck className="w-3.5 h-3.5 text-blue-400" />
                  Verifying {verifInfo.queued} new email(s) with MailerSend…
                </p>
                {verifJobs.map(j => {
                  const pct = j.total ? Math.round(Math.min(j.verified_count || 0, j.total) / j.total * 100) : 0;
                  return (
                    <div key={j.job_id} className="space-y-1">
                      <div className="flex justify-between text-[10px] text-muted-foreground">
                        <span>{j.status === "completed" ? "Verification complete" : j.status === "failed" ? `Failed — ${j.error}` : "Verifying in background…"}</span>
                        <span className="tabular-nums">{j.verified_count || 0}/{j.total}</span>
                      </div>
                      <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                        <div className={`h-full rounded-full ${j.status === "failed" ? "bg-red-500" : j.status === "completed" ? "bg-emerald-500" : "bg-blue-500"}`}
                          style={{ width: `${j.status === "completed" ? 100 : pct}%` }} />
                      </div>
                      {j.status === "completed" && Object.keys(j.counts || {}).length > 0 && (
                        <p className="text-[10px] text-muted-foreground">{Object.entries(j.counts).map(([k, v]) => `${v} ${k.replace(/_/g, " ")}`).join(" · ")}</p>
                      )}
                    </div>
                  );
                })}
                <p className="text-[10px] text-muted-foreground">You can close this dialog — verification continues in the background (track it in the Verification tab).</p>
              </div>
            )}
            <Button onClick={() => { onOpenChange(false); reset(); }}>Done</Button>
          </div>
        ) : (
          <>
            <div className="flex gap-2">
              <Button variant={mode === "csv" ? "default" : "outline"} size="sm" onClick={() => setMode("csv")} data-testid="mode-csv">CSV File</Button>
              <Button variant={mode === "paste" ? "default" : "outline"} size="sm" onClick={() => setMode("paste")} data-testid="mode-paste">Paste Emails</Button>
              <div className="flex-1" />
              <Select value={targetList} onValueChange={setTargetList}>
                <SelectTrigger className="w-48" data-testid="import-target-list"><SelectValue placeholder="Add to list (optional)" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No list</SelectItem>
                  {lists.map(l => <SelectItem key={l.list_id} value={l.list_id}>{l.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {mode === "csv" && !preview && (
              <div className="space-y-3">
                <input type="file" accept=".csv,text/csv" onChange={onFile} className="text-sm" data-testid="csv-file-input" />
                <p className="text-[11px] text-muted-foreground">or paste CSV content:</p>
                <Textarea rows={6} value={csvText} onChange={(e) => setCsvText(e.target.value)}
                  placeholder={"email,first_name,company\njohn@acme.com,John,Acme Garage Doors"} data-testid="csv-text-input" />
                <Button disabled={!csvText.trim() || busy} onClick={() => doPreview()} data-testid="preview-import-btn">Preview</Button>
              </div>
            )}

            {mode === "csv" && preview && (
              <div className="space-y-3">
                <p className="text-xs text-muted-foreground">{preview.total_rows} rows detected. Map columns:</p>
                {preview.stats && (
                  <div className="flex flex-wrap gap-1.5" data-testid="import-stats">
                    <Badge className="bg-emerald-500/15 text-emerald-500 text-[10px]">{preview.stats.valid_new} new valid emails</Badge>
                    <Badge className="bg-amber-500/15 text-amber-500 text-[10px]">{preview.stats.duplicates_existing} already in contacts</Badge>
                    {preview.stats.duplicates_in_file > 0 && (
                      <Badge className="bg-sky-500/15 text-sky-400 text-[10px]">{preview.stats.duplicates_in_file} duplicated inside file</Badge>
                    )}
                    <Badge className="bg-red-500/15 text-red-400 text-[10px]">{preview.stats.invalid_or_empty} invalid / empty</Badge>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-2">
                  {FIELD_OPTIONS.map(f => (
                    <div key={f} className="flex items-center gap-2">
                      <Label className="w-28 text-xs">{f}{f === "email" && " *"}</Label>
                      <Select value={mapping[f] !== undefined ? String(mapping[f]) : "skip"}
                        onValueChange={(v) => {
                          const n = { ...mapping };
                          if (v === "skip") delete n[f]; else n[f] = parseInt(v);
                          setMapping(n);
                          if (f === "email") doPreview(n); // refresh outcome stats
                        }}>
                        <SelectTrigger className="h-8 text-xs" data-testid={`map-${f}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="skip">— skip —</SelectItem>
                          {preview.headers.map((h, i) => <SelectItem key={i} value={String(i)}>{h}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>
                <div className="border border-border rounded overflow-x-auto">
                  <table className="w-full text-[11px]">
                    <thead><tr>{preview.headers.map((h, i) => <th key={i} className="p-1.5 text-left border-b border-border text-muted-foreground">{h}</th>)}</tr></thead>
                    <tbody>{preview.preview_rows.slice(0, 5).map((r, ri) => (
                      <tr key={ri} className="border-b border-border/30">{r.map((cell, ci) => <td key={ci} className="p-1.5">{cell}</td>)}</tr>
                    ))}</tbody>
                  </table>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => setPreview(null)}>Back</Button>
                  <Button size="sm" disabled={mapping.email === undefined || busy} onClick={doImport} data-testid="confirm-import-btn">
                    Import {preview.total_rows} rows
                  </Button>
                </div>
                {mapping.email === undefined && <Badge className="bg-red-500/15 text-red-400 text-[10px]">Email column mapping required</Badge>}
              </div>
            )}

            {mode === "paste" && (
              <div className="space-y-3">
                <Textarea rows={8} value={pasteText} onChange={(e) => setPasteText(e.target.value)}
                  placeholder={"one@company.com\ntwo@company.com, three@company.com"} data-testid="paste-emails-input" />
                <Button disabled={!pasteText.trim() || busy} onClick={doPasteImport} data-testid="paste-import-btn">Import Emails</Button>
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
